import React, { useEffect, useMemo, useState } from 'react';
import { Button, Col, FormGroup, Input, Label, Row, Table, UncontrolledTooltip } from 'reactstrap';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cloneDeep, debounce, sumBy } from 'lodash-es';
import { useSelector } from 'react-redux';

import Modal from '../../../ui/Modal';
import { preformatFloat, locale } from '../../../utilities/invoiceUtils';
import { formatChannelText } from '../../../utilities/textsFormatters';

const blockEl = 'invoices__add-charges-modal';

const AddChargesModal = ({
  isOpen,
  onClose,
  onSave,
  onUpdateInvoice,
  currencySymbol,
  variant,
  paymentProcessorType,
  invoices: originalInvoices,
  setIndex,
  setIsAddChargesModalOpen,
  hideAdditionalCode,
}) => {
  const [invoices, setInvoices] = useState([]);
  const [invoiceIndex, setInvoiceIndex] = useState(0);
  const [isSavingCharges, setIsSavingCharges] = useState(false);
  const [showRestrictedText, setShowRestrictedText] = useState(false);
  const [additionalCharges, setAdditionalCharges] = useState();
  const [usPayrixUser, setUsPayrixUser] = useState(false);
  const [isLotVariant, setIsLotVariant] = useState(variant === 'lot');
  const [isLotAdditionalVariant, setIsLotAdditionalVariant] = useState(variant === 'lot-additional');
  const headingTotalText = 'Total Additional Charges';
  const { paymentProcessingEnabled } = useSelector(state => state.house);
  const [hidePreviousLink, setHidePreviousLink] = useState(false);
  const [floorByInvoiceArray, setFloorByInvoiceArray] = useState([]);

  const toggle = () => {
    setIsAddChargesModalOpen(false);
  };

  const closeBtn = (
    <button className="close" onClick={toggle} type="button">
      &times;
    </button>
  );

  useEffect(() => {
    if (typeof originalInvoices === 'undefined'
      || !Array.isArray(originalInvoices)
      || !originalInvoices.length) {
      return;
    }
    if (setIndex > 0) {
      setInvoiceIndex(setIndex);
    }

    setInvoices(cloneDeep(originalInvoices));
    let onLoadTotal = 0;

    if (isLotAdditionalVariant) {
      onLoadTotal = sumBy(
        originalInvoices.charges,
        (item) => parseFloat(preformatFloat(item.additionalTax)) + parseFloat(preformatFloat(item.additionalOther)) + parseFloat(preformatFloat(item.additionalShipping)) + parseFloat(preformatFloat(item.additionalInsurance))
      );
    } else {
      onLoadTotal = sumBy(
        originalInvoices.charges,
        (item) => parseFloat(preformatFloat(item.tax)) + parseFloat(preformatFloat(item.other)) + parseFloat(preformatFloat(item.shipping)) + parseFloat(preformatFloat(item.insurance))
      );
    }

    setAdditionalCharges(onLoadTotal.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  }, [originalInvoices]);

  const currentInvoice = useMemo(() => invoices[invoiceIndex], [invoices, invoiceIndex]);
  const isFloorBidder = () => currentInvoice.channel.trim().indexOf('Floor') > -1;

  const isLastInvoice = useMemo(() => {
    if (invoices.length < 2) {
      return true;
    }

    return invoiceIndex === invoices.length - 1;
  }, [invoices, invoiceIndex]);

  useEffect(() => {
    if (invoices.length > 0) {
      invoices.forEach(invoice => {
        if (invoice.showRestrictedModal) setHidePreviousLink(true);
      });
    }
  }, [invoices]);

  const handleOnSaveChargePerIndex = (index) => (event) => {
    event.persist();

    const deferred = debounce((e) => {
      const chargeFieldName = e.target.name;
      const chargeFieldValue = parseFloat(preformatFloat(e.target.value) || '0.00')
        .toFixed(2)
        .toLocaleString();

      setInvoices((prev) => prev.map((item, itemIndex) => {
        if (invoiceIndex === itemIndex) {
          return {
            ...item,
            charges: item.charges.map((itemCharges, itemChargesIndex) => {
              if (itemChargesIndex === index) {
                return {
                  ...itemCharges,
                  [chargeFieldName]: chargeFieldValue,
                };
              }

              return itemCharges;
            }),
          };
        }

        return item;
      }));
    }, 500);

    deferred(event);
  };

  const checkForModalSwitch = (isPrev = false) => {
    let switchModals = false;
    let compareInvoice = null;
    if (isPrev && invoiceIndex > 0) {
      compareInvoice = invoices[invoiceIndex - 1];
    } else if (!isPrev && !isLastInvoice) {
      compareInvoice = invoices[invoiceIndex + 1];
    }
    switchModals = (currentInvoice.showRestrictedModal !== compareInvoice.showRestrictedModal);
    return switchModals;
  };

  const handleOnSave = (goToNext) => async () => {
    try {
      setIsSavingCharges(true);

      await onSave(currentInvoice, isLotAdditionalVariant, floorByInvoiceArray);

      if (goToNext) {
        if (checkForModalSwitch()) {
          onClose('lot', true, invoiceIndex + 1);
        } else {
          setInvoiceIndex((invoiceIndex + 1));
        }
      } else {
        onClose('lot', false);
      }

      onUpdateInvoice(currentInvoice);
    } finally {
      setIsSavingCharges(false);
    }
  };

  const handleOnBlur = (e) => {
    const safeValue = parseFloat(Number(e.target.value) || 0).toFixed(2);
    e.target.value = safeValue;
  };

  useEffect(() => {
    if (currentInvoice) {
      let isAddl = false;
      let paidByPP = false;
      let currentInvoiceAllPaid = false;
      if (!hideAdditionalCode) {
        if (currentInvoice.charges.some(item => !item.restricted && (item.paymentMethod.trim() === 'paidByWePay' || item.paymentMethod.trim() === 'ACH'))) {
          paidByPP = true;
        }
        if (currentInvoice.charges.every(item => item.paid || item.suppress)) {
          currentInvoiceAllPaid = true;
        }
        isAddl = ((currentInvoiceAllPaid && !currentInvoice.isPendingPayment) && paidByPP && paymentProcessingEnabled && paymentProcessorType === 'p');
        setIsLotAdditionalVariant(isAddl);
        setIsLotVariant(!isAddl);
      }

      if (currentInvoice.charges.some(item => item.restricted)) {
        setShowRestrictedText(true);
      }
      let currentTotal = 0;
      if (isAddl) {
        currentTotal = sumBy(
          currentInvoice.charges,
          (item) => parseFloat(preformatFloat(item.additionalTax)) + parseFloat(preformatFloat(item.additionalOther)) + parseFloat(preformatFloat(item.additionalShipping)) + parseFloat(preformatFloat(item.additionalInsurance))
        );
      } else {
        currentTotal = sumBy(
          currentInvoice.charges,
          (item) => parseFloat(preformatFloat(item.tax)) + parseFloat(preformatFloat(item.other)) + parseFloat(preformatFloat(item.shipping)) + parseFloat(preformatFloat(item.insurance))
        );
      }
      setAdditionalCharges(currentTotal.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
      setUsPayrixUser(paymentProcessingEnabled && paymentProcessorType === 'p' && currentInvoice.country === 'US');
    }
  }, [currentInvoice, invoiceIndex]);

  const clearInputField = (e) => {
    if (e.currentTarget.value === '0.00' || e.currentTarget.value === '0') {
      e.currentTarget.value = '';
    }
    e.currentTarget.classList.remove('zero-value-color');
  };

  const setFieldClasses = (suppress, value) => {
    let returnString = '';
    if (suppress) returnString += 'disabled';
    if (value === '0.00') {
      returnString += returnString.length > 0 ? ' zero-value-color' : 'zero-value-color';
    }
    return returnString;
  };

  const handlePreviousClick = () => {
    if (checkForModalSwitch(true)) {
      onClose('lot', true, invoiceIndex - 1);
    } else {
      setInvoiceIndex((prev) => prev - 1);
    }
  };

  const handleFloorByInvoiceData = (e) => {
    handleOnBlur(e);
    const arr = floorByInvoiceArray;
    let addNewLot = true;
    if (arr.length > 0) {
      arr.forEach(lot => {
        if (lot.invoiceID === currentInvoice.id) {
          lot[e.currentTarget.name] = Number(e.currentTarget.value);
          addNewLot = false;
        }
      });
    }
    if (addNewLot) {
      const newObject = {};
      newObject.invoiceID = currentInvoice.id;
      newObject[e.currentTarget.name] = Number(e.currentTarget.value);
      arr.push(newObject);
    }

    setFloorByInvoiceArray(arr);
  };

  const renderDataTable = () => {
    if (isLotVariant || isLotAdditionalVariant) {
      const textAdditionalPrefix = isLotAdditionalVariant ? 'Add\'l ' : '';
      return (
        <Row>
          <Col>
            <Table bordered responsive className={`${blockEl}__table`}>
              <thead>
                <tr className="fees-row">
                  <th>Lot</th>
                  <th>Hammer</th>
                  {isFloorBidder() && !currentInvoice.allPaid && (
                    <>
                      <th>BP</th>
                      <th> Live Fee</th>
                    </>
                  )}
                  <th>{textAdditionalPrefix}Tax</th>
                  <th>{textAdditionalPrefix}S/H</th>
                  <th>{textAdditionalPrefix}Insurance</th>
                  <th>
                    {textAdditionalPrefix}Other{' '}
                    <FontAwesomeIcon
                      icon="question-circle"
                      id="otherTooltip"
                      style={{ cursor: 'pointer' }}
                    />
                    <UncontrolledTooltip placement="right" target="otherTooltip">
                      This field is for adding or discounting any additional fees.
                      Please use a minus (-) to indicate a discount or adjustment.
                    </UncontrolledTooltip>
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentInvoice.charges.map(
                  (
                    { lotId, lotDisplayName, lotURL, tax, shipping, insurance, restricted, paid, suppress, other, hammer, livefee, premium, lotTitle, additionalTax, additionalShipping, additionalInsurance, additionalOther, additionalPaid, additionalPending, pendingLot, isInvShipping },
                    index
                  ) => (
                    <tr key={lotId}>
                      <td id={`lot_${lotId}`} style={{ cursor: 'pointer' }}>
                        <a
                          href={lotURL}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {lotDisplayName}
                          {restricted && paymentProcessingEnabled && ('*')}
                        </a>
                        <UncontrolledTooltip placement="right" target={`lot_${lotId}`}>
                          {lotTitle}
                        </UncontrolledTooltip>
                      </td>
                      {(isFloorBidder() && !paid && !isLotAdditionalVariant) ? (
                        <>
                          <td>
                            <Input
                              type="number"
                              name="hammer"
                              placeholder=""
                              min="0"
                              onChange={handleOnSaveChargePerIndex(index)}
                              defaultValue={preformatFloat(hammer)}
                              onFocus={(e) => clearInputField(e)}
                              onBlur={(e) => handleOnBlur(e)}
                              lotid={lotId}
                            />
                          </td>
                          <td>
                            <Input
                              type="number"
                              name="premium"
                              placeholder=""
                              min="0"
                              onChange={handleOnSaveChargePerIndex(index)}
                              defaultValue={preformatFloat(premium)}
                              onFocus={(e) => clearInputField(e)}
                              onBlur={(e) => handleOnBlur(e)}
                              lotid={lotId}
                            />
                          </td>
                          <td>
                            <Input
                              type="number"
                              name="livefee"
                              placeholder=""
                              min="0"
                              onChange={handleOnSaveChargePerIndex(index)}
                              defaultValue={preformatFloat(livefee)}
                              onFocus={(e) => clearInputField(e)}
                              onBlur={(e) => handleOnBlur(e)}
                              lotid={lotId}
                            />
                          </td>
                        </>
                      ) : (
                        <td>
                          <div className="mt-2">{currencySymbol}{hammer}</div>
                        </td>
                      )}
                      <td>
                        <Input
                          type="number"
                          name={isLotAdditionalVariant ? 'additionalTax' : 'tax'}
                          placeholder=""
                          min="0"
                          onChange={handleOnSaveChargePerIndex(index)}
                          defaultValue={paid ? preformatFloat(additionalTax) : preformatFloat(tax)}
                          onFocus={(e) => clearInputField(e)}
                          disabled={isLotAdditionalVariant ? (usPayrixUser || additionalPaid || suppress || restricted || additionalPending) : !restricted && (paid || suppress || usPayrixUser || pendingLot)}
                          className={isLotAdditionalVariant ? setFieldClasses(suppress, additionalTax) : setFieldClasses(suppress, tax)}
                          onBlur={(e) => handleOnBlur(e)}
                        />
                      </td>
                      <td>
                        <Input
                          type="number"
                          name={isLotAdditionalVariant ? 'additionalShipping' : 'shipping'}
                          placeholder=""
                          min="0"
                          onChange={handleOnSaveChargePerIndex(index)}
                          defaultValue={paid ? preformatFloat(additionalShipping) : preformatFloat(shipping)}
                          onFocus={(e) => clearInputField(e)}
                          disabled={isLotAdditionalVariant ? additionalPaid || suppress || restricted || additionalPending : (paid || suppress || pendingLot)}
                          className={isLotAdditionalVariant ? setFieldClasses(suppress, additionalShipping) : setFieldClasses(suppress, shipping)}
                          onBlur={(e) => handleOnBlur(e)}
                        />
                      </td>
                      <td>
                        <Input
                          type="number"
                          name={isLotAdditionalVariant ? 'additionalInsurance' : 'insurance'}
                          placeholder=""
                          min="0"
                          onChange={handleOnSaveChargePerIndex(index)}
                          defaultValue={paid ? preformatFloat(additionalInsurance) : preformatFloat(insurance)}
                          onFocus={(e) => clearInputField(e)}
                          disabled={isLotAdditionalVariant ? additionalPaid || suppress || restricted || additionalPending : (paid || suppress || pendingLot)}
                          className={isLotAdditionalVariant ? setFieldClasses(suppress, additionalInsurance) : setFieldClasses(suppress, insurance)}
                          onBlur={(e) => handleOnBlur(e)}
                        />
                      </td>
                      <td>
                        <Input
                          type="number"
                          name={isLotAdditionalVariant ? 'additionalOther' : 'other'}
                          placeholder=""
                          min="0"
                          onChange={handleOnSaveChargePerIndex(index)}
                          defaultValue={paid ? preformatFloat(additionalOther) : preformatFloat(other)}
                          onFocus={(e) => clearInputField(e)}
                          disabled={isLotAdditionalVariant ? additionalPaid || suppress || restricted || additionalPending : (paid || suppress || pendingLot)}
                          className={isLotAdditionalVariant ? setFieldClasses(suppress, additionalOther) : setFieldClasses(suppress, other)}
                          onBlur={(e) => handleOnBlur(e)}
                        />
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </Table>
            {showRestrictedText && paymentProcessingEnabled && (
              <div>
                *this item is restricted and cannot be paid for online
              </div>
            )}
          </Col>
        </Row>
      );
    }

    return (
      <>
        <Row>
          <Col>
            <FormGroup>
              <Label for="additionalTax">Additional Tax</Label>
              <Input type="number" id="additionalTax" placeholder="0.00" />
            </FormGroup>
          </Col>
          <Col>
            <FormGroup>
              <Label for="additionalSH">Additional S/H</Label>
              <Input type="number" id="additionalSH" placeholder="0.00" />
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col>
            <FormGroup>
              <Label for="additionalInsurance">Additional Insurance</Label>
              <Input
                type="number"
                id="additionalInsurance"
                placeholder="0.00"
              />
            </FormGroup>
          </Col>
          <Col>
            <FormGroup>
              <Label for="additionalOther">
                Additional Other <FontAwesomeIcon icon="question-circle" />
              </Label>
              <Input type="number" id="additionalOther" placeholder="0.00" />
            </FormGroup>
          </Col>
        </Row>
      </>
    );
  };

  const renderActions = () => {
    if ((isLotVariant || isLotAdditionalVariant) && invoices.length > 1) {
      return (
        <Row className={`${blockEl}__actions`}>
          <Col sm="2">
            <p>
              {invoiceIndex + 1} of {invoices.length}
            </p>
          </Col>
          <Col sm="auto">
            {invoiceIndex > 0 && !hidePreviousLink && (
              <Button
                color="link"
                onClick={() => handlePreviousClick()}
              >
                Previous invoice
              </Button>
            )}
            {invoiceIndex > 0 && (
            <Button
              color={isLastInvoice ? 'primary' : 'secondary'}
              onClick={handleOnSave(false)}
              disabled={isSavingCharges}
            >
              Save &amp; Close
            </Button>
            )}
            {!isLastInvoice && (
              <Button
                color="primary"
                onClick={handleOnSave(true)}
                disabled={isSavingCharges}
              >
                Save &amp; Next
              </Button>
            )}
          </Col>
        </Row>
      );
    }

    return (
      <Button color="primary" onClick={handleOnSave()}>
        Save Changes
      </Button>
    );
  };

  if (!invoices.length) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      className={isFloorBidder() && !currentInvoice.allPaid ? 'invoices__add-charges-modal modal-floor-bidder w-75' : blockEl}
      toggle={toggle}
      onClosed={() => setInvoiceIndex(0)}
    >
      <Modal.Header toggle={toggle} close={closeBtn}>Add/Edit Charges</Modal.Header>
      <Modal.Body>
        <Row className={`${blockEl}__details`}>
          <Col sm="8">
            <h4>{currentInvoice.name} <span className="small">(Paddle #{currentInvoice.paddleNumber})</span></h4>
            <p>
              Invoice #{currentInvoice.id} | {currentInvoice.location} | {formatChannelText(currentInvoice.channel)}
            </p>
          </Col>
          <Col sm="auto">
            <h4>{headingTotalText}</h4>
            <p>{currencySymbol}{additionalCharges}</p>
          </Col>
        </Row>
        {renderDataTable()}
      </Modal.Body>
      <Modal.Footer>{renderActions()}</Modal.Footer>
    </Modal>
  );
};

AddChargesModal.propTypes = {
  invoices: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      id: PropTypes.number.isRequired,
      outstandingBalance: PropTypes.string.isRequired,
      paddleNumber: PropTypes.number.isRequired,
      location: PropTypes.string.isRequired,
      channel: PropTypes.string.isRequired,
      charges: PropTypes.arrayOf(
        PropTypes.shape({
          lotId: PropTypes.number,
          lotDisplayName: PropTypes.string,
          tax: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
          shipping: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
          insurance: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
          other: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
          suppress: PropTypes.bool,
        })
      ).isRequired,
    })
  ),
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  onUpdateInvoice: PropTypes.func,
  variant: PropTypes.oneOf(['lot', 'lot-additional', 'invoice']),
};

AddChargesModal.defaultProps = {
  invoices: [],
  isOpen: false,
  onClose: Function.prototype,
  onSave: Function.prototype,
  onUpdateInvoice: Function.prototype,
  variant: 'lot',
};

export default AddChargesModal;
